import React, { useState, useEffect, useCallback } from "react";
import { GetData, PostData, PatchData } from "../../services/api";
import DataTable from "react-data-table-component";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  CardBody,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  FormGroup,
  Label,
} from "reactstrap";
import { Link, useParams } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Swal from "sweetalert2";
import IntegrationModal from "./IntegrationModal";

// ─── Add to list modal ───────────────────────────────────────────────────────

const AddToListModal = ({ isOpen, toggle, contact, currentListId, onDone }) => {
  const [lists, setLists] = useState([]);
  const [targetListId, setTargetListId] = useState("");
  const [move, setMove] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    GetData("/contact-lists")
      .then(res => setLists((res.data || []).filter(l => l.id !== currentListId)))
      .catch(() => setLists([]));
  }, [isOpen, currentListId]);

  const handleSave = async () => {
    if (!targetListId) return;
    setSaving(true);
    try {
      if (move) {
        // Move atómico: saca de la lista actual y mete a la destino en una sola llamada
        // (publica contact_removed_from_list + contact_added_to_list).
        await PostData(`/contact-lists/customers/${contact.id}/move`, {
          fromListId: currentListId,
          toListId: targetListId,
        });
      } else {
        await PostData(`/contact-lists/${targetListId}/customers`, { customerId: contact.id });
      }
      Swal.fire({
        icon: "success",
        title: move ? "Contacto movido" : "Contacto agregado",
        text: `${contact.name} ${contact.lastName} fue ${move ? "movido" : "agregado"} a la lista`,
        timer: 2000,
        showConfirmButton: false,
      });
      onDone(move);
      toggle();
    } catch (err) {
      const msg = err?.response?.data?.error?.message || "No se pudo completar la operación";
      Swal.fire({ icon: "error", title: "Error", text: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        <i className="mdi mdi-playlist-plus me-2" />
        {contact ? `${contact.name} ${contact.lastName}` : "Contacto"}
      </ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label style={{ fontSize: 13 }}>Selecciona la lista destino</Label>
          <Input
            type="select"
            value={targetListId}
            onChange={e => setTargetListId(e.target.value)}
          >
            <option value="">— Elige una lista —</option>
            {lists.map(l => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </Input>
        </FormGroup>
        <FormGroup check>
          <Input
            type="checkbox"
            id="move-check"
            checked={move}
            onChange={e => setMove(e.target.checked)}
          />
          <Label check for="move-check" style={{ fontSize: 13 }}>
            Mover (quitar de la lista actual)
          </Label>
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={toggle}>Cancelar</Button>
        <Button color="primary" onClick={handleSave} disabled={saving || !targetListId}>
          {saving ? (
            <><span className="spinner-border spinner-border-sm me-1" style={{ width: "0.75rem", height: "0.75rem" }} />Guardando...</>
          ) : (
            move ? "Mover" : "Agregar"
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────

const ContactListDetails = () => {
  const { id } = useParams();
  const [data, setData] = useState([]);
  const [listName, setListName] = useState("");
  const [filterText, setFilterText] = useState("");
  const [integrationOpen, setIntegrationOpen] = useState(false);
  const [addListModal, setAddListModal] = useState({ open: false, contact: null });

  const fetchData = useCallback(async () => {
    // Fetch independently — a failure on one must not block the other
    try {
      const contactsRes = await GetData(`/contact-lists/${id}/customers`);
      setData(contactsRes?.data || []);
    } catch (_) {}

    try {
      const listRes = await GetData(`/contact-lists/${id}`);
      setListName(listRes?.data?.name || "");
    } catch (_) {}
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRemoveFromList = async (contact) => {
    const result = await Swal.fire({
      title: "¿Quitar de esta lista?",
      text: `${contact.name} ${contact.lastName} será quitado de ${listName}. El contacto no se elimina.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#E9553E",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, quitar",
      cancelButtonText: "Cancelar",
    });
    if (!result.isConfirmed) return;
    try {
      await PatchData(`/contact-lists/${id}/customers/${contact.id}/remove`, {});
      setData(prev => prev.filter(c => c.id !== contact.id));
    } catch (_) {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo quitar el contacto" });
    }
  };

  const filtered = data.filter(c =>
    `${c.name} ${c.lastName} ${c.email} ${c.phone}`.toLowerCase().includes(filterText.toLowerCase())
  );

  const columns = [
    {
      name: <span className="font-weight-bold fs-13">Nombre</span>,
      selector: row => row.name,
      sortable: true,
      cell: row => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{row.name} {row.lastName}</div>
          <div style={{ fontSize: 11, color: "#888" }}>{row.email}</div>
        </div>
      ),
    },
    {
      name: <span className="font-weight-bold fs-13">Teléfono</span>,
      selector: row => row.phone,
      sortable: true,
      cell: row => <span style={{ fontSize: 13 }}>{row.phone || "—"}</span>,
    },
    {
      name: <span className="font-weight-bold fs-13">Tags</span>,
      cell: row => (
        <div className="d-flex flex-wrap gap-1">
          {(row.tags || []).slice(0, 3).map((t, i) => (
            <Badge key={i} color="light" style={{ color: "#495057", fontSize: 10, border: "1px solid #dee2e6" }}>{t}</Badge>
          ))}
          {(row.tags || []).length > 3 && (
            <Badge color="light" style={{ color: "#888", fontSize: 10 }}>+{row.tags.length - 3}</Badge>
          )}
        </div>
      ),
    },
    {
      name: <span className="font-weight-bold fs-13">Acción</span>,
      width: "60px",
      cell: row => (
        <UncontrolledDropdown>
          <DropdownToggle tag="button" className="btn btn-sm btn-link p-0">
            <i className="ri-more-fill" style={{ fontSize: 16 }} />
          </DropdownToggle>
          <DropdownMenu end>
            <DropdownItem tag={Link} to={`/leads/${row.id}`}>
              <i className="ri-eye-fill me-2 text-muted" />Ver detalle
            </DropdownItem>
            <DropdownItem onClick={() => setAddListModal({ open: true, contact: row })}>
              <i className="mdi mdi-playlist-plus me-2 text-muted" />Agregar / Mover a lista
            </DropdownItem>
            <DropdownItem divider />
            <DropdownItem className="text-danger" onClick={() => handleRemoveFromList(row)}>
              <i className="mdi mdi-playlist-remove me-2" />Quitar de esta lista
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
    },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="IHubG"
          breadcrumbItem={
            <><Link to="/contact-lists">Listas de Contactos</Link> / {listName}</>
          }
        />

        {/* Header actions */}
        <Row className="mb-3 align-items-center">
          <Col>
            <h5 className="mb-0">{listName}</h5>
          </Col>
          <Col className="col-auto d-flex gap-2">
            <Button
              color="light"
              size="sm"
              onClick={() => setIntegrationOpen(true)}
              style={{ border: "1px solid #dee2e6" }}
            >
              <i className="mdi mdi-api me-1" />
              Integración
            </Button>
          </Col>
        </Row>

        {/* Search + table */}
        <Card>
          <CardBody>
            <Row className="mb-3">
              <Col>
                <div className="search-box" style={{ maxWidth: 320 }}>
                  <input
                    type="text"
                    className="form-control search"
                    placeholder="Buscar contacto..."
                    value={filterText}
                    onChange={e => setFilterText(e.target.value)}
                  />
                  <i className="ri-search-line search-icon" />
                </div>
              </Col>
              <Col className="col-auto">
                <small className="text-muted">{filtered.length} contacto{filtered.length !== 1 ? "s" : ""}</small>
              </Col>
            </Row>
            <DataTable
              columns={columns}
              data={filtered}
              noDataComponent={<span className="py-4 text-muted">Sin contactos en esta lista</span>}
              pagination
              paginationPerPage={25}
              paginationRowsPerPageOptions={[25, 50, 100]}
              highlightOnHover
              pointerOnHover
            />
          </CardBody>
        </Card>
      </Container>

      {/* Modals */}
      <IntegrationModal
        isOpen={integrationOpen}
        toggle={() => setIntegrationOpen(false)}
        listId={id}
        listName={listName}
      />
      <AddToListModal
        isOpen={addListModal.open}
        toggle={() => setAddListModal({ open: false, contact: null })}
        contact={addListModal.contact}
        currentListId={id}
        onDone={(wasMoved) => wasMoved && fetchData()}
      />
    </div>
  );
};

export default ContactListDetails;
